import { computed, ref, watch } from 'vue'
import { useBudgetData } from './useBudgetData'

export function useTodayCalculations() {
  const { currentMonthTransactions, dailyBudget, currentDate, budgetStartDate, isPartialMonth } = useBudgetData()

  // Constants
  const ROLLOVER_THRESHOLD = 0.01 // Ignore differences less than 1 cent

  // Cache for date calculations to improve performance
  const budgetCache = ref(new Map())

  // Clear cache when transactions change (additions, deletions, or modifications)
  watch(() => currentMonthTransactions.value, () => {
    budgetCache.value.clear()
  }, { deep: true })

  // Clear cache when current date changes (new day)
  watch(() => currentDate.value.toDateString(), () => {
    budgetCache.value.clear()
  })

  /**
   * Check if two dates are the same day
   * @param {Date} date1 - First date to compare
   * @param {Date} date2 - Second date to compare
   * @returns {boolean} True if dates are the same day
   */
  const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
  }

  /**
   * Check if a date is today
   * @param {string|Date} date - Date to check (string or Date object)
   * @returns {boolean} True if date is today
   */
  const isToday = (date) => {
    return isSameDay(new Date(date), currentDate.value)
  }

  /**
   * Calculate net spending for a date range
   * @param {Date|null} startDate - Start of range (inclusive), null for no start limit
   * @param {Date|null} endDate - End of range (exclusive), null for no end limit
   * @returns {number} Net spending (debits + savings - credits)
   */
  const calculateSpendingForDateRange = (startDate, endDate) => {
    return currentMonthTransactions.value
      .filter(t => {
        const tDate = new Date(t.date)
        if (startDate && tDate < startDate) return false
        if (endDate && tDate >= endDate) return false
        return true
      })
      .reduce((sum, t) => {
        if (t.type === 'debit' || t.type === 'savings') {
          return sum + t.amount
        } else if (t.type === 'credit') {
          return sum - t.amount
        }
        return sum
      }, 0)
  }

  /**
   * Calculate net spending on a specific date
   * @param {Date} targetDate - The date to calculate spending for
   * @returns {number} Net spending on that date (debits + savings - credits)
   */
  const calculateSpendingOnDate = (targetDate) => {
    return currentMonthTransactions.value
      .filter(t => isSameDay(new Date(t.date), targetDate))
      .reduce((sum, t) => {
        if (t.type === 'debit' || t.type === 'savings') {
          return sum + t.amount
        } else if (t.type === 'credit') {
          return sum - t.amount
        }
        return sum
      }, 0)
  }

  /**
   * Calculate days passed since budget start date
   * @param {Date} targetDate - The date to calculate days passed for
   * @returns {number} Number of days since budget started (0 for first day)
   */
  const calculateDaysPassed = (targetDate) => {
    if (isPartialMonth.value && budgetStartDate.value) {
      const startDate = new Date(budgetStartDate.value)
      return targetDate.getDate() - startDate.getDate()
    }
    return targetDate.getDate() - 1
  }

  // Today's debits (regular expenses only, not savings)
  const todayDebits = computed(() => {
    return currentMonthTransactions.value
      .filter(t => t.type === 'debit' && isToday(t.date))
      .reduce((sum, t) => sum + t.amount, 0)
  })

  // Today's credits (income)
  const todayCredits = computed(() => {
    return currentMonthTransactions.value
      .filter(t => t.type === 'credit' && isToday(t.date))
      .reduce((sum, t) => sum + t.amount, 0)
  })

  // Today's savings contributions (actual money moved to savings)
  const todaySavingsContributions = computed(() => {
    return currentMonthTransactions.value
      .filter(t => t.type === 'savings' && isToday(t.date))
      .reduce((sum, t) => sum + t.amount, 0)
  })

  // Today's net spending (debits + savings - credits)
  const todayNetSpending = computed(() => {
    return todayDebits.value + todaySavingsContributions.value - todayCredits.value
  })

  /**
   * Today's remaining budget calculation
   * 
   * Calculates how much budget is left for today by subtracting today's net spending
   * from the fixed daily budget allocation. This represents the amount available
   * to spend for the rest of the day.
   * 
   * Formula: Daily Budget - Today's Net Spending
   * 
   * @returns {number} Remaining budget for today (can be negative if overspent)
   * 
   * Examples:
   * - Daily budget: $50, Today's spending: $30 → Remaining: $20
   * - Daily budget: $50, Today's spending: $60 → Remaining: -$10 (overspent)
   * - Daily budget: $50, Today's income: $20, spending: $40 → Remaining: $30
   */
  const todayRemainingBudget = computed(() => {
    return dailyBudget.value - todayNetSpending.value
  })

  // Today's available budget with rollover from previous days
  const todayAvailableBudget = computed(() => {
    const today = currentDate.value
    const daysPassed = calculateDaysPassed(today)

    // If it's the first day of tracking, no rollover calculation needed
    if (daysPassed === 0) {
      return dailyBudget.value - todayNetSpending.value
    }

    // Calculate how much should have been spent by yesterday (since tracking started)
    const expectedSpentByYesterday = dailyBudget.value * daysPassed

    // Calculate actual net spending by yesterday (excluding today)
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    const actualSpentByYesterday = calculateSpendingForDateRange(null, todayStart)

    // Calculate rollover (positive if underspent, negative if overspent)
    const rollover = expectedSpentByYesterday - actualSpentByYesterday

    // Today's budget with rollover adjustment, minus today's spending
    return dailyBudget.value + rollover - todayNetSpending.value
  })

  // Calculate available budget for a specific date (with caching)
  const calculateAvailableBudgetForDate = (targetDate) => {
    const target = new Date(targetDate)
    const cacheKey = target.toISOString().split('T')[0] // YYYY-MM-DD format

    // Check cache first
    if (budgetCache.value.has(cacheKey)) {
      return budgetCache.value.get(cacheKey)
    }

    // Calculate if not cached
    const daysPassed = calculateDaysPassed(target)

    let result
    // If it's the first day, no rollover
    if (daysPassed === 0) {
      const daySpending = calculateSpendingOnDate(target)
      result = dailyBudget.value - daySpending
    } else {
      // Calculate expected and actual spending up to the day before target
      const expectedSpentByPreviousDay = dailyBudget.value * daysPassed
      const targetStart = new Date(target.getFullYear(), target.getMonth(), target.getDate())
      const actualSpentByPreviousDay = calculateSpendingForDateRange(null, targetStart)
      const rollover = expectedSpentByPreviousDay - actualSpentByPreviousDay

      // Calculate spending on target date
      const targetDaySpending = calculateSpendingOnDate(target)

      result = dailyBudget.value + rollover - targetDaySpending
    }

    // Store in cache
    budgetCache.value.set(cacheKey, result)
    return result
  }

  // Calculate 7-day average of available budget and trend
  const sevenDayBudgetTrend = computed(() => {
    const today = currentDate.value
    const availableBudgets = []

    // Collect available budget for the last 7 days (including today)
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today)
      date.setDate(date.getDate() - i)

      // Only include dates within the current month
      if (date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()) {
        const availableBudget = calculateAvailableBudgetForDate(date)
        availableBudgets.push(availableBudget)
      }
    }

    // Calculate average
    const average = availableBudgets.length > 0
      ? availableBudgets.reduce((sum, val) => sum + val, 0) / availableBudgets.length
      : 0

    // Compare today's available budget to the average
    const todayAvailable = todayAvailableBudget.value
    const difference = todayAvailable - average
    const percentChange = average !== 0 ? (difference / Math.abs(average)) * 100 : 0

    return {
      average,
      todayAvailable,
      difference,
      percentChange,
      isTrendingUp: difference > 0,
      isTrendingDown: difference < 0,
      daysIncluded: availableBudgets.length
    }
  })

  // Budget status information for UI display
  const budgetStatus = computed(() => {
    const actualRemaining = todayAvailableBudget.value

    // Ahead / behind relative to safe daily spending rate
    const aheadAmount =
      actualRemaining - dailyBudget.value

    // How many normal days of spending remain at the safe rate
    const runwayDays =
      dailyBudget.value > 0
        ? actualRemaining / dailyBudget.value
        : 0

    return {
      dailyRemaining: todayRemainingBudget.value,
      availableRemaining: actualRemaining,

      // Replaces previous "rolloverAmount"
      rolloverAmount: aheadAmount,

      runwayDays,

      isOverDailyBudget: todayRemainingBudget.value < 0,
      hasRollover: Math.abs(aheadAmount) > ROLLOVER_THRESHOLD,
      isBehindForMonth: aheadAmount < 0,
      isAheadForMonth: aheadAmount > 0
    }
  })

  // Today's transactions
  const todayTransactions = computed(() => {
    return currentMonthTransactions.value.filter(t => isToday(t.date))
  })

  return {
    todayDebits,
    todayCredits,
    todaySavingsContributions,
    todayNetSpending,
    todayRemainingBudget,
    todayAvailableBudget,
    budgetStatus,
    todayTransactions,
    sevenDayBudgetTrend,
    isToday
  }
}